import { Chat } from '../types';
import { API_BASE_URL } from './api';
import { createAuthenticatedRequestOptions } from '../utils/apiUtils';

// Use the centralized function for creating authenticated request options
const createRequestOptions = createAuthenticatedRequestOptions;

export const chatPersistenceService = {
  async loadChats(): Promise<Chat[]> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/saved`, createRequestOptions({
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      }));
      if (!response.ok) throw new Error(`Failed to load chats: ${response.status}`);
      const data = await response.json();
      return Array.isArray(data.chats) ? data.chats : [];
    } catch (error) {
      // console.error('Error loading chats from API:', error);
      return [];
    }
  },

  async saveChat(chat: Chat): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/save`, createRequestOptions({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ chat })
      }));
      if (!response.ok) throw new Error(`Failed to save chat: ${response.status}`);
    } catch (error) {
      // console.error('Error saving chat to API:', error);
    }
  },

  async deleteChat(chatId: string): Promise<void> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/${chatId}`, createRequestOptions({
        method: 'DELETE',
        headers: {
          'Content-Type': 'application/json',
        }
      }));
      if (!response.ok) throw new Error(`Failed to delete chat: ${response.status}`);
    } catch (error) {
      // console.error('Error deleting chat from API:', error);
      throw error;
    }
  },

  async bulkDeleteChats(chatIds: string[]): Promise<{ success: boolean; deletedCount: number; failedDeletes?: Array<{ chatId: string; error: string }> }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/bulk-delete`, createRequestOptions({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ chatIds })
      }));
      
      if (!response.ok) {
        throw new Error(`Failed to bulk delete chats: ${response.status}`);
      }
      
      const result = await response.json();
      return result;
    } catch (error) {
      // console.error('Error bulk deleting chats from API:', error);
      throw error;
    }
  },

  async getChatById(chatId: string): Promise<Chat | null> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/${chatId}`, createRequestOptions({
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      }));
      if (!response.ok) {
        if (response.status === 404) return null;
        throw new Error(`Failed to get chat: ${response.status}`);
      }
      const data = await response.json();
      return data.chat || null;
    } catch (error) {
      // console.error('Error getting chat from API:', error);
      return null;
    }
  },

  async toggleChatStar(chatId: string): Promise<{ success: boolean; isStarred: boolean }> {
    try {
      const response = await fetch(`${API_BASE_URL}/api/chat/${chatId}/star`, createRequestOptions({
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      }));
      if (!response.ok) throw new Error(`Failed to toggle chat star: ${response.status}`);
      const data = await response.json();
      return { success: data.success, isStarred: data.isStarred };
    } catch (error) {
      // console.error('Error toggling chat star:', error);
      throw error;
    }
  },

  sortChatsByTimestamp(chats: Chat[]): Chat[] {
    return chats.sort((a, b) => {
      const timestampA = a.timestamp ? new Date(a.timestamp) : new Date(0);
      const timestampB = b.timestamp ? new Date(b.timestamp) : new Date(0);
      if (timestampA.getTime() === 0 && timestampB.getTime() === 0) return 0;
      if (timestampA.getTime() === 0) return 1;
      if (timestampB.getTime() === 0) return -1;
      return timestampB.getTime() - timestampA.getTime();
    });
  },
}; 